import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clapperboard, GalleryHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProBadge } from '@/components/ProBadge';
import { ClipPostMenu } from '@/components/publishing/ClipPostMenu';
import { useClipPostActions } from '@/hooks/use-clip-post-actions';
import { useClipExtraActions } from '@/hooks/use-clip-extra-actions';
import { clipStudioPath } from '@/lib/freecut-studio/studioPath';
import type { PublishUiStatus } from '@/lib/publishing/types';

interface Props {
  clipId: string;
  /** Rendered and not expired: gates the post menu (queue/TikTok/IG/FB/YT). */
  canPublish: boolean;
  /** Gates the actions that need no render, "Carrossel de frases" and FreeCut
   *  Studio, independently of `canPublish`: both read the source video
   *  (`canMakeQuoteCarousel`). Defaults to `canPublish` for callers that do
   *  not compute it. */
  canMakeCarousel?: boolean;
  compact?: boolean;
  /** The "Carrossel de frases" entry point navigates away from whatever flow
   *  is hosting this component (e.g. the /calendar scheduling picker), so
   *  callers whose click should stay inside their own flow opt out. Default
   *  true for the ordinary clip-card call sites. */
  showCarouselAction?: boolean;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  /** Shown only when the viewer may post to Facebook (`useSocialPostingPlatforms`). */
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
}

const stopCardClick = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

/** Shared, isolated controls and live status regions for every clip-card shape. */
export const ClipPublishingControls: React.FC<Props> = ({
  clipId,
  canPublish,
  canMakeCarousel = canPublish,
  compact = false,
  showCarouselAction = true,
  ...postProps
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const post = useClipPostActions({ clipId, canPublish, compact, ...postProps });
  // "Carrossel de frases" and FreeCut Studio need no render and no
  // social_publishing; see useClipExtraActions.
  const { showCarousel, showStudio } = useClipExtraActions({ canMakeCarousel, showCarouselAction });
  const iconClassName = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const triggerClassName = compact
    ? 'min-w-0 w-full h-auto min-h-11 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-tight'
    : 'w-full';

  // Nothing to show at all (e.g. the /calendar picker opts out of the
  // carousel action and has no social_publishing post controls either).
  if (!post.showPostControls && !showCarousel && !showStudio) return null;

  return (
    <div
      data-testid="clip-publishing-controls"
      className={`w-full min-w-0 ${compact ? 'space-y-1.5' : 'space-y-2'}`}
    >
      <ClipPostMenu post={post} />
      {showStudio && (
        <div className="w-full min-w-0" onClick={stopCardClick}>
          <Button
            type="button"
            variant="outline"
            data-testid="clip-freecut-studio-action"
            size={compact ? 'sm' : undefined}
            className={triggerClassName}
            onClick={() => navigate(clipStudioPath(clipId))}
          >
            <Clapperboard className={iconClassName} />
            <span>{t('clipStudio.open')}</span>
          </Button>
        </div>
      )}
      {showCarousel && (
        <div className="w-full min-w-0" onClick={stopCardClick}>
          <Button
            type="button"
            variant="outline"
            data-testid="clip-quote-carousel-action"
            size={compact ? 'sm' : undefined}
            className={triggerClassName}
            onClick={() => navigate(`/clips/${clipId}/carousel`)}
          >
            <GalleryHorizontal className={iconClassName} />
            <span>{t('quoteCarousel.title')}</span>
            <ProBadge className={compact ? 'px-1.5 py-0 text-[9px] leading-4' : undefined} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClipPublishingControls;
